/**
 * FuelRefillsPage — company driver logs fuel purchases for a
 * company-owned vehicle. Schema source-of-truth: `fuelRefillSchema`
 * in server/routes/cargoCompanyDriver.js (Joi).
 *
 *   GET /api/cargo/me/company-driver/fuel-refills
 *   POST .../fuel-refills  body: { vehicleId, fuelType, amountSyp,
 *     liters?, odometerKm, photoUrl?, driverNotes? }
 *
 * Server-side gating: the vehicle MUST be is_company_owned=true and
 * owned by the calling driver. The button only renders for vehicles
 * matching both — but the server enforces independently.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Fuel, Plus, Loader, Receipt, AlertCircle } from 'lucide-react';
import { useFuelRefills, useCreateFuelRefill } from '@/hooks/useFuelRefills';
import { vehiclesApi } from '@/api/vehicles';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import type { CreateFuelRefillInput, FuelRefill, FuelType, FuelEvaluation } from '@/types/companyExtras';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

const EVAL_LABEL: Record<FuelEvaluation, { text: string; color: string }> = {
  normal:     { text: 'مقبولة',          color: 'bg-success/10 text-success' },
  wasteful:   { text: 'استهلاك عالي',     color: 'bg-warning/10 text-warning' },
  tampering:  { text: 'مشكوك بها',        color: 'bg-error/10 text-error' },
};

export default function FuelRefillsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: refills, isLoading } = useFuelRefills();
  const create = useCreateFuelRefill();
  const [showForm, setShowForm] = useState(false);

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
              <Fuel size={20} /> {t('fuel_refills_title', 'تعبئة الوقود')}
            </h1>
            <p className="text-text-secondary text-sm">
              {t('fuel_refills_subtitle', 'سجّل تعبئاتك من المركبات المملوكة للشركة. الإدارة تراجع كل عملية.')}
            </p>
          </div>
          {!showForm && (
            <Button variant="primary" onClick={() => setShowForm(true)}>
              <Plus size={18} className="ml-1" />
              {t('add', 'إضافة')}
            </Button>
          )}
        </div>

        {showForm && (
          <RefillForm
            loading={create.isPending}
            onCancel={() => setShowForm(false)}
            onSubmit={async (values) => {
              await create.mutateAsync(values);
              setShowForm(false);
            }}
          />
        )}

        {isLoading ? (
          <div className="flex justify-center py-12 text-text-tertiary">
            <Loader size={28} className="animate-spin" />
          </div>
        ) : !refills || refills.length === 0 ? (
          !showForm && (
            <div className="bg-surface border border-border rounded-2xl p-10 text-center">
              <Fuel size={48} className="mx-auto text-text-tertiary mb-3" />
              <p className="text-text-secondary">
                {t('fuel_refills_empty', 'لم تُسجّل أيّ تعبئة بعد.')}
              </p>
            </div>
          )
        ) : (
          <div className="space-y-2">
            {refills.map((r) => <RefillCard key={r.id} refill={r} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function RefillCard({ refill }: { refill: FuelRefill }) {
  const evalBadge = refill.evaluation ? EVAL_LABEL[refill.evaluation] : null;

  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-bold text-text-primary">
            {refill.liters != null ? `${Number(refill.liters).toFixed(2)} L · ` : ''}
            {refill.fuel_type === 'diesel' ? 'مازوت' : 'بنزين'}
          </div>
          <div className="text-sm text-text-secondary">
            {Number(refill.amount_syp).toLocaleString()} ل.س
          </div>
          <div className="text-xs text-text-tertiary mt-0.5">
            عداد: {refill.odometer_km.toLocaleString()} km
          </div>
        </div>
        {evalBadge ? (
          <span className={`text-xs px-2 py-1 rounded-full ${evalBadge.color} font-bold`}>
            {evalBadge.text}
          </span>
        ) : (
          <span className="text-xs px-2 py-1 rounded-full bg-info/10 text-info font-bold">
            بانتظار المراجعة
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-text-tertiary">
        <span>{new Date(refill.created_at).toLocaleString('ar-SY')}</span>
      </div>
      {refill.driver_notes && (
        <p className="text-xs text-text-secondary mt-2 italic">{refill.driver_notes}</p>
      )}
      {refill.evaluation_note && (
        <p className="text-xs text-warning mt-1 italic">
          ملاحظة الإدارة: {refill.evaluation_note}
        </p>
      )}
      {refill.photo_url && (
        <a
          href={refill.photo_url}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
        >
          <Receipt size={12} /> صورة العداد
        </a>
      )}
    </div>
  );
}

function RefillForm({
  loading, onSubmit, onCancel,
}: {
  loading: boolean;
  onSubmit: (v: CreateFuelRefillInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'mine'],
    queryFn: () => vehiclesApi.listMy(),
  });

  const [v, setV] = useState<CreateFuelRefillInput>({
    vehicleId: 0,
    fuelType: 'diesel',
    amountSyp: 0,
    odometerKm: 0,
  });
  const [error, setError] = useState<string | null>(null);

  const eligibleVehicles = (vehicles || []).filter((veh: any) => veh.is_company_owned);

  return (
    <form
      className="bg-surface border border-border rounded-2xl p-5 mb-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!v.vehicleId) return setError(t('error_vehicle_required', 'اختر مركبة'));
        if (v.amountSyp <= 0) return setError(t('error_amount_required', 'المبلغ إلزامي'));
        if (v.odometerKm < 0) return setError(t('error_odometer_required', 'قراءة العداد إلزامية'));
        onSubmit(v);
      }}
    >
      {error && (
        <div className="bg-error/10 text-error rounded-xl p-3 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <Field label={t('vehicle', 'المركبة') + ' *'}>
        <select
          value={v.vehicleId || ''}
          onChange={(e) => setV({ ...v, vehicleId: Number(e.target.value) })}
          className={FIELD_CLASSES}
          required
        >
          <option value="">{t('select_vehicle', 'اختر مركبة...')}</option>
          {eligibleVehicles.map((veh: any) => (
            <option key={veh.id} value={veh.id}>
              {veh.brand || ''} {veh.model || ''} — {veh.plate_number || veh.plate_text || ''}
            </option>
          ))}
        </select>
        {eligibleVehicles.length === 0 && (
          <p className="text-xs text-warning mt-1">
            {t('no_company_vehicles', 'لا مركبات مملوكة للشركة باسمك. تواصل مع شركتك.')}
          </p>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('fuel_type', 'نوع الوقود')}>
          <select
            value={v.fuelType}
            onChange={(e) => setV({ ...v, fuelType: e.target.value as FuelType })}
            className={FIELD_CLASSES}
          >
            <option value="diesel">مازوت</option>
            <option value="gasoline">بنزين</option>
          </select>
        </Field>
        <TextField
          label={t('liters_optional', 'اللترات (اختياري)')}
          type="number"
          value={v.liters ?? ''}
          onChange={(e) => setV({ ...v, liters: e.target.value === '' ? undefined : Number(e.target.value) })}
          min={0} step={0.01} dir="ltr"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <TextField
          label={t('amount_syp', 'المبلغ (ل.س)') + ' *'}
          type="number"
          value={v.amountSyp || ''}
          onChange={(e) => setV({ ...v, amountSyp: Number(e.target.value) })}
          min={1} required dir="ltr"
        />
        <TextField
          label={t('odometer_km', 'قراءة العداد (km)') + ' *'}
          type="number"
          value={v.odometerKm || ''}
          onChange={(e) => setV({ ...v, odometerKm: Number(e.target.value) })}
          min={0} required dir="ltr"
        />
      </div>

      <TextField
        label={t('photo_url', 'رابط صورة العداد (موصى به)')}
        type="url"
        value={v.photoUrl || ''}
        onChange={(e) => setV({ ...v, photoUrl: e.target.value })}
        dir="ltr"
        placeholder="https://..."
      />

      <Field label={t('driver_notes', 'ملاحظات السائق')}>
        <textarea
          value={v.driverNotes || ''}
          onChange={(e) => setV({ ...v, driverNotes: e.target.value })}
          className={FIELD_CLASSES + ' min-h-[60px]'}
          maxLength={500}
        />
      </Field>

      <div className="flex gap-2 pt-1">
        <Button
          variant="primary"
          loading={loading}
          type="submit"
          disabled={eligibleVehicles.length === 0}
        >
          {t('save', 'حفظ')}
        </Button>
        <Button variant="ghost" type="button" onClick={onCancel}>
          {t('cancel', 'إلغاء')}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}
